import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { DownloadIcon, SearchIcon, FilterIcon, ChevronDownIcon, XIcon, CalendarIcon } from 'lucide-react';
interface Column {
  header: string;
  accessor: string;
}
interface DataTableProps {
  columns: Column[];
  data: any[];
  pagination?: boolean;
  searchable?: boolean;
  exportable?: boolean;
}
export const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  pagination = false,
  searchable = false,
  exportable = false
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const filtersRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Close filters when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target as Node) && buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setShowFilters(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const rowsPerPage = 10;
  // Get unique values for each filterable column
  const filterOptions = useMemo(() => {
    const options: Record<string, Set<string>> = {};
    columns.forEach(column => {
      if (column.accessor !== 'id' && column.accessor !== 'refNo') {
        options[column.accessor] = new Set();
      }
    });
    data.forEach(row => {
      Object.keys(options).forEach(key => {
        if (row[key]) {
          options[key].add(row[key].toString());
        }
      });
    });
    // Convert Sets to sorted Arrays
    const result: Record<string, string[]> = {};
    Object.keys(options).forEach(key => {
      result[key] = Array.from(options[key]).sort();
    });
    return result;
  }, [data, columns]);
  const filteredData = useMemo(() => {
    return data.filter(row => {
      // Apply search filter
      const matchesSearch = !searchTerm || columns.some(column => {
        const value = row[column.accessor];
        return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      });
      // Apply status filter
      const matchesStatus = statusFilter === 'All Statuses' || row.status && row.status === statusFilter || row.paymentStatus && row.paymentStatus === statusFilter;
      // Apply date filter
      let matchesDateRange = true;
      if (startDate || endDate) {
        const dateColumns = ['date']; // Add any other date column names here
        const rowDate = dateColumns.reduce((foundDate, colName) => {
          return foundDate || (row[colName] ? new Date(row[colName]) : null);
        }, null);
        if (rowDate) {
          if (startDate && new Date(startDate) > rowDate) {
            matchesDateRange = false;
          }
          if (endDate && new Date(endDate) < rowDate) {
            matchesDateRange = false;
          }
        }
      }
      // Apply additional filters
      const matchesFilters = Object.keys(activeFilters).every(key => {
        return !activeFilters[key] || row[key]?.toString() === activeFilters[key];
      });
      return matchesSearch && matchesStatus && matchesDateRange && matchesFilters;
    });
  }, [data, searchTerm, columns, statusFilter, startDate, endDate, activeFilters]);
  const paginatedData = useMemo(() => {
    if (!pagination) return filteredData;
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, currentPage, pagination]);
  const totalPages = useMemo(() => {
    return Math.ceil(filteredData.length / rowsPerPage);
  }, [filteredData]);
  const handleExport = () => {
    const headers = columns.map(col => col.header).join(',');
    const rows = filteredData.map(row => {
      return columns.map(col => row[col.accessor]).join(',');
    }).join('\n');
    const csvContent = `data:text/csv;charset=utf-8,${headers}\n${rows}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleFilterChange = (accessor: string, value: string) => {
    setActiveFilters(prev => ({
      ...prev,
      [accessor]: value
    }));
  };
  const clearFilters = () => {
    setActiveFilters({});
    setStartDate('');
    setEndDate('');
  };
  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length + (startDate || endDate ? 1 : 0);
  return <div data-id="element-91">
      {(searchable || exportable) && <div className="flex flex-col md:flex-row gap-4 mb-4 items-start md:items-center justify-between" data-id="element-92">
          {/* Search Box */}
          <div className="relative flex-grow max-w-md w-full" data-id="element-93">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-94" />
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-md bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-200" placeholder="Search records..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-95" />
          </div>
          {/* Filter Controls */}
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto" data-id="element-96">
            {/* Date Filter */}
            <div className="flex items-center gap-2 flex-wrap" data-id="element-97">
              <div className="relative" data-id="element-98">
                <input type="date" className="px-3 py-2 border border-gray-200 rounded-md text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-200" value={startDate} onChange={e => setStartDate(e.target.value)} placeholder="Start Date" data-id="element-99" />
                <CalendarIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-100" />
              </div>
              <span className="text-gray-500" data-id="element-101">to</span>
              <div className="relative" data-id="element-102">
                <input type="date" className="px-3 py-2 border border-gray-200 rounded-md text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-200" value={endDate} onChange={e => setEndDate(e.target.value)} placeholder="End Date" data-id="element-103" />
                <CalendarIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-104" />
              </div>
            </div>
            {/* More Filters Button */}
            <div className="relative" data-id="element-105">
              <button ref={buttonRef} className={`flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 ${activeFilterCount > 0 ? 'text-blue-600 border-blue-300 bg-blue-50' : 'text-blue-600'} rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-200`} onClick={() => setShowFilters(!showFilters)} data-id="element-106">
                <FilterIcon className="w-4 h-4" data-id="element-107" />
                More Filters
                {activeFilterCount > 0 && <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium rounded-full bg-blue-100 text-blue-800" data-id="element-108">
                    {activeFilterCount}
                  </span>}
              </button>
              {showFilters && <div ref={filtersRef} className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-md shadow-lg z-10" data-id="element-109">
                  <div className="p-3 border-b border-gray-200 flex justify-between items-center" data-id="element-110">
                    <h3 className="font-medium text-gray-700" data-id="element-111">Filters</h3>
                    <button className="text-gray-400 hover:text-gray-600" onClick={clearFilters} data-id="element-112">
                      Reset Filters
                    </button>
                  </div>
                  <div className="p-3 max-h-80 overflow-y-auto" data-id="element-113">
                    {/* Status Filter */}
                    <div className="mb-4" data-id="element-114">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-115">
                        Status
                      </label>
                      <select className="w-full p-2 border border-gray-200 rounded-md text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-116">
                        <option value="All Statuses" data-id="element-117">All</option>
                        <option value="Paid" data-id="element-118">Paid</option>
                        <option value="Overdue" data-id="element-119">Overdue</option>
                        <option value="Appealed" data-id="element-120">Appealed</option>
                      </select>
                    </div>
                    {Object.keys(filterOptions).map(accessor => {
                const columnName = columns.find(col => col.accessor === accessor)?.header || accessor;
                // Skip status columns and date column as they're handled separately
                if (accessor === 'status' || accessor === 'paymentStatus' || accessor === 'date') return null;
                return <div key={accessor} className="mb-4" data-id="element-121">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-122">
                            {columnName}
                          </label>
                          <select className="w-full p-2 border border-gray-200 rounded-md text-sm" value={activeFilters[accessor] || ''} onChange={e => handleFilterChange(accessor, e.target.value)} data-id="element-123">
                            <option value="" data-id="element-124">All</option>
                            {filterOptions[accessor]?.map(option => <option key={option} value={option} data-id="element-125">
                                {option}
                              </option>)}
                          </select>
                        </div>;
              })}
                  </div>
                  <div className="p-3 border-t border-gray-200 flex justify-end" data-id="element-126">
                    <div className="flex gap-2" data-id="element-127">
                      <button onClick={() => {
                  setCurrentPage(1);
                  setShowFilters(false);
                }} className="flex-1 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-128">
                        Apply Filter
                      </button>
                      <button onClick={clearFilters} className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-129">
                        Clear
                      </button>
                    </div>
                  </div>
                </div>}
            </div>
            {/* Apply/Clear Filter Buttons */}
            {exportable && <div className="flex gap-2 ml-auto md:ml-0" data-id="element-130">
                <button onClick={() => setCurrentPage(1)} className="px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-131">
                  Apply Filter
                </button>
                <button onClick={clearFilters} className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-132">
                  Clear
                </button>
              </div>}
          </div>
        </div>}
      <div className="overflow-x-auto" data-id="element-133">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-134">
          <thead className="bg-gray-50" data-id="element-135">
            <tr data-id="element-136">
              {columns.map((column, index) => <th key={index} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-137">
                  {column.header}
                </th>)}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-138">
            {paginatedData.length > 0 ? paginatedData.map((row, rowIndex) => <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-139">
                  {columns.map((column, colIndex) => <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-140">
                      {column.accessor === 'status' || column.accessor === 'paymentStatus' ? <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-medium ${row[column.accessor] === 'Paid' || row[column.accessor] === 'Paid in Full' ? 'bg-green-100 text-green-800' : row[column.accessor] === 'Overdue' || row[column.accessor] === 'Delinquent' ? 'bg-red-100 text-red-800' : row[column.accessor] === 'Appealed' || row[column.accessor] === 'In Appeal' ? 'bg-yellow-100 text-yellow-800' : row[column.accessor] === 'Partially Paid' ? 'bg-blue-100 text-blue-800' : row[column.accessor] === 'Unpaid' ? 'bg-gray-100 text-gray-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-141">
                          {row[column.accessor]}
                        </span> : column.accessor === 'recordType' ? <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-medium ${row[column.accessor] === 'Birth' ? 'bg-blue-100 text-blue-800' : row[column.accessor] === 'Death' ? 'bg-red-100 text-red-800' : row[column.accessor] === 'Marriage' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-142">
                          {row[column.accessor]}
                        </span> : row[column.accessor]}
                    </td>)}
                </tr>) : <tr data-id="element-143">
                <td colSpan={columns.length} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-144">
                  No data available
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
      {pagination && totalPages > 1 && <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6" data-id="element-145">
          <div className="flex-1 flex justify-between sm:hidden" data-id="element-146">
            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-147">
              Previous
            </button>
            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-148">
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-149">
            <div data-id="element-150">
              <p className="text-sm text-gray-700" data-id="element-151">
                Showing{' '}
                {Math.min((currentPage - 1) * rowsPerPage + 1, filteredData.length)}{' '}
                to {Math.min(currentPage * rowsPerPage, filteredData.length)} of{' '}
                {filteredData.length} entries
              </p>
            </div>
            <div className="flex items-center space-x-2" data-id="element-152">
              <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className={`px-4 py-2 border rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-153">
                Previous
              </button>
              {totalPages <= 5 ?
          // Show all pages if 5 or fewer
          [...Array(totalPages)].map((_, index) => <button key={index} onClick={() => setCurrentPage(index + 1)} className={`w-10 h-10 flex items-center justify-center rounded-md ${currentPage === index + 1 ? 'bg-blue-50 text-blue-600 border border-blue-500' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-154">
                    {index + 1}
                  </button>) :
          // Show limited pages with ellipsis for many pages
          <>
                  {currentPage > 2 && <button onClick={() => setCurrentPage(1)} className="w-10 h-10 flex items-center justify-center rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50" data-id="element-155">
                      1
                    </button>}
                  {currentPage > 3 && <span className="text-gray-500" data-id="element-156">...</span>}
                  {currentPage > 1 && <button onClick={() => setCurrentPage(currentPage - 1)} className="w-10 h-10 flex items-center justify-center rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50" data-id="element-157">
                      {currentPage - 1}
                    </button>}
                  <button className="w-10 h-10 flex items-center justify-center rounded-md bg-blue-50 text-blue-600 border border-blue-500" data-id="element-158">
                    {currentPage}
                  </button>
                  {currentPage < totalPages && <button onClick={() => setCurrentPage(currentPage + 1)} className="w-10 h-10 flex items-center justify-center rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50" data-id="element-159">
                      {currentPage + 1}
                    </button>}
                  {currentPage < totalPages - 2 && <span className="text-gray-500" data-id="element-160">...</span>}
                  {currentPage < totalPages - 1 && <button onClick={() => setCurrentPage(totalPages)} className="w-10 h-10 flex items-center justify-center rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50" data-id="element-161">
                      {totalPages}
                    </button>}
                </>}
              <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className={`px-4 py-2 border rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-162">
                Next
              </button>
            </div>
          </div>
        </div>}
      {/* Action buttons */}
      <div className="mt-4 flex justify-between" data-id="element-163">
        <button className="px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50" data-id="element-164">
          Close
        </button>
        <div className="flex space-x-3" data-id="element-165">
          <button className="px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50" data-id="element-166">
            Print Report
          </button>
          <button className="px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50" data-id="element-167">
            Export PDF
          </button>
          <button className="px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50" onClick={handleExport} data-id="element-168">
            Export CSV
          </button>
        </div>
      </div>
    </div>;
};